
import React, { useState } from 'react';

import styled from 'styled-components';

const kPrompt = "Drop in JSON, URL encoded text, base64 data, log exerpts, etc. Hit Ctrl/Cmd+Enter to analyze the input, Ctrl/Cmd+C to clear.";

const PasteBoxContainer = styled.div`
	padding: 1rem;
	margin: 1rem 0;
	border-radius: 8px;
	background-color: ${props => props.theme.colors.bg};
`;

const PasteInput = styled.textarea`
    width: 100%;
    min-height: 150px;
    padding: 0.5rem;
    border: 1px solid transparent;
    border-radius: 4px;
    font-size: 1rem;
    line-height: 1.5;
    resize: vertical;
    font-family: inherit;
    background: transparent;
    color: inherit;
    transition: all 0.2s ease;

    &::placeholder {
        color: ${props => props.theme.colors.textDim};
    }

    &:hover {
        background: ${props => props.theme.colors.hover};
    }

    &:focus {
        outline: none;
        background: ${props => props.theme.colors.focus};
        border-color: ${props => props.theme.colors.border};
    }
`;

const Instructions = styled.div`
	padding: 0.25rem;
	margin: 0.25rem 0;
	font-size: 0.8rem;
	font-style: italic;
`;

const ButtonRow = styled.div`
    display: flex;
    gap: 0.5rem;
    margin-top: 1rem;
`;

const Button = styled.button`
    padding: 0.5rem 1rem;
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 4px;
    background: transparent;
    color: inherit;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${props => props.theme.colors.focus};
    }

    &:active {
        background: ${props => props.theme.colors.border};
    }
`;

const ToggleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-left: auto;
`;

const ToggleSwitch = styled.label`
    position: relative;
    display: inline-block;
    width: 36px;
    height: 20px;
`;

const ToggleInput = styled.input`
    opacity: 0;
    width: 0;
    height: 0;
`;

const ToggleSlider = styled.span`
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255,255,255,0.1);
    transition: .4s;
    border-radius: 20px;
    border: 1px solid rgba(255,255,255,0.2);

    &:before {
        position: absolute;
        content: "";
        height: 14px;
        width: 14px;
        left: 2px;
        bottom: 2px;
        background-color: rgba(255,255,255,0.8);
        transition: .4s;
        border-radius: 50%;
    }

    input:checked + & {
        background-color: rgba(255,255,255,0.3);
    }

    input:checked + &:before {
        transform: translateX(16px);
    }
`;

const ToggleLabel = styled.span`
    color: inherit;
    font-size: 0.8rem;
    opacity: 0.8;
`;


function looksLikeUrlEncodedText ( text )
{
	if ( text.includes ( "%" ) )
	{
		// check that every instance of a % is followed by two hex digits
		var re = /%[0-9A-Fa-f]{2}/g;
		var matches = text.match ( re );
		return matches !== null;
	}
	return false;
}

function looksLikeJson ( text )
{
	text = text.trim ();
	return ( ( text.startsWith ( "{" ) && text.endsWith ( "}" ) ) ||
	( text.startsWith ( "[" ) && text.endsWith ( "]" ) ) );
}

export function PasteBox ( props )
{
	var [ userInputText, setUserInputText ] = useState ( '' );
	var [ processedText, setProcessedText ] = useState ( '' );
	var [ showProcessed, setShowProcessed ] = useState ( false );

	var [ jsonTransform, setJsonTransform ] = useState ( true );
	var [ urlTransform, setUrlTransform ] = useState ( true );

	const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
	const modifierKey = isMac ? '⌘' : 'Ctrl';

	const processUserText = () => {

		// start with user input...
		var workingText = userInputText.trim ();

		// url transform
		if ( urlTransform && looksLikeUrlEncodedText ( workingText ) )
		{
			console.log ( "URL decode..." );
			workingText = decodeURIComponent ( workingText );
		}

		// json transform
		if ( jsonTransform && looksLikeJson ( workingText ) )
		{
			console.log ( "JSON decode..." );
			try {
				workingText = JSON.stringify ( JSON.parse ( workingText ), null, 2 );
			} catch ( e ) {
				console.log ( "exception trying to parse json" );
			}
		}

		// maybe replace the text
		if ( workingText !== userInputText )
		{
			setProcessedText ( workingText );
			setShowProcessed ( true );
		}
	};

	const handleClear = () => {
        setUserInputText('');
		setProcessedText('');
		setShowProcessed ( false );
    };

	const handleInputChange = (e) => {
        setUserInputText ( e.target.value );
		setShowProcessed ( false );
    };

	const handleKeyDown = (e) => {

		if (e.ctrlKey || e.metaKey) {
			switch(e.code) {
				case 'Enter':
					e.preventDefault();
					processUserText();
					break;
				case 'Backspace':
					e.preventDefault();
					handleClear();
					break;
				}
		}
	};

	return (
		<PasteBoxContainer>
			<PasteInput
				value={showProcessed ? processedText : userInputText}
				onChange={handleInputChange}
				onKeyDown={handleKeyDown}
				type="text"
				placeholder={kPrompt}
				readOnly={showProcessed}
				autoCorrect='off'
				autoCapitalize='off'
				spellCheck='false'
			/>
			<ButtonRow>
				<Button onClick={handleClear}>Clear</Button>
				<Button onClick={processUserText}>Process</Button>
				<Button onClick={() => { setShowProcessed(false); }}>Original</Button>
				{/* <Button>Copy</Button> */}
				<ToggleContainer>

					<ToggleLabel>URL Encoding</ToggleLabel>
					<ToggleSwitch>
						<ToggleInput
							type="checkbox"
							checked={urlTransform}
							onChange={e => setUrlTransform(e.target.checked)}
						/>
						<ToggleSlider />
					</ToggleSwitch>

					<ToggleLabel>JSON</ToggleLabel>
					<ToggleSwitch>
						<ToggleInput
							type="checkbox"
							checked={jsonTransform}
							onChange={e => setJsonTransform(e.target.checked)}
						/>
						<ToggleSlider />
					</ToggleSwitch>
				</ToggleContainer>
			</ButtonRow>

			<Instructions><span>{modifierKey}-Enter: process</span>; <span>{modifierKey}-Backspace: clear</span></Instructions>
		</PasteBoxContainer>
	);
}
